import * as $ from 'jquery';
import { Observable, Subject, BehaviorSubject } from 'common/barrels/rx';
import { serialDisposable, disposableSubscription, disposable, Lazy } from 'common/barrels/core';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import * as rxui from 'common/rxui/core';
import { FilteredLookupItemsRequested } from 'runtime/events/filteredlookupitemsrequested';
import { LookupItemByDisplayRequested } from 'runtime/events/lookupitembydisplayrequested';
import { LookupItemRequested } from 'runtime/events/lookupitemrequested';
import { Color } from 'common/color';
import { div } from 'common/dom/primitives';
import { LookupItem } from 'runtime/data/lookupitem';
import { registerBinding } from 'runtime/bindingengine';

import 'runtime/styles/controls/autocompletelist.less';
import { maxDefaultZero } from 'common/functions/array';

class LookupResult {
    constructor(public filter: string, public items: LookupItem[]) { }
}

var navKeys = { 40: 1, 38: -1 };
var maxListHeight = 300;
var listMargin = 20;
var defaultPopulationDelay = 250;

export class AutoCompleteList extends Control {
    private value = new BehaviorSubject<LookupItem>(null);
    private setValues = new Subject<string>();
    private listDisposable = serialDisposable(null);
    private $input: JQuery;
    private $icon: JQuery;
    private listOpen = false;
    private readyTrigger = new Subject<any>();
    private initialized = false;
    private updating = false;
    private focusGroup: BehaviorSubject<JQuery>;

    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);

        this.$element.addClass('control-container autocompletelist');
        this.$input = $('<input />')
            .css('text-align', this.config['content.TextAlignment'].toLowerCase())
            .conditionalCss(this.config['content.FontFamily'], 'font-family')
            .conditionalCss(this.config['content.FontStyle'], 'font-style')
            .css('font-size', this.config['content.FontSize'] + 'px')
            .css('font-weight', this.config['content.FontWeight'].toLowerCase())
            .conditionalCss(this.config['content.Foreground'], 'color',() => Color.splitArgb(this.config['content.Foreground'])[0])
            .applyBackground(this.config['content.Background'])
            .appendTo(this.$element);

        this.focusGroup = new BehaviorSubject<JQuery>(this.$input);

        this.$icon = $('<i />').addClass('fa fa-long-arrow-down').appendTo(this.$element);

        var valueSet = this.setValues.withLatestFrom(this.value, (x, y) => ({ newKey: x, currentItem: y })).share();

        const checkValueTrigger = this.focusGroup.switchMap(x => rxui.groupBlur(x)).map(() => this.$input.val());

        var input = rxui.from(this.$input, 'propertychange input').filter(() => !this.updating).share();

        this.applyTooltip(this.$input.add(this.$icon));

        var nullOrEmpty = x => x === null || x === '';

        var textValue = Observable.of<boolean>(true).merge(Observable.fromEvent(this.$input, 'change keyup paste').map(() => true)).merge(this.value.map(() => true)).map(() => this.$input.val()).distinctUntilChanged().pairwise().share();

        const focusOut = Observable.fromEvent(this.$input, 'focusout');
        const focusIn = Observable.fromEvent(this.$input, 'focusin');

        let items: Lazy<Observable<LookupResult>> = new Lazy(() => Observable.never<LookupResult>());

        const typingTrigger = Observable.fromEvent(this.$input, 'focusin')
            .switchMap(() => input
                .map(() => this.$input.val())
                .distinctUntilChanged()
                .map(() => true)
                .debounceTime(this.config['content.MinimumPopulateDelay'] || defaultPopulationDelay)
                .merge(rxui.filteredKeys(this.$input, [$.ui.keyCode.DOWN]).filter(() => !this.listOpen).map(() => true))
                .filter(() => this.$input.val().length >= this.config['content.MinimumPrefixLength'])
                .takeUntil(Observable.fromEvent(this.$input, 'focusout'))
            );

        const clickTrigger = rxui.mousedownleft(this.$icon)
            .do(e => e.preventDefault())
            .do(() => this.$input.focus())
            .filter(() => this.listOpen === false && this.derivedEnabledState() !== false)
            .filter(() => this.$input.val().length >= this.config['content.MinimumPrefixLength']);

        const [first, second] = typingTrigger.merge(clickTrigger).prioritize();

        this.subscriptions = disposableSubscription(
            first.map(() => this.$input.val())
                .distinctUntilChanged()
                .subscribe(x => items = new Lazy(() => this.getLookupItems(x).publishReplay().refCount())),

            second.switchMap(() =>
                Observable.using(
                    () => ({ unsubscribe: () => this.removeLoading() }),
                    () => {
                        this.showLoading();
                        this.closeList();
                        return this.getLookupItems(this.$input.val()).takeUntil(focusOut);
                    }
                )
            )
            .delay(0)
            .subscribe(x => this.renderList(x)),

            input.map(() => this.$input.val()).filter(x => x.length < this.config['content.MinimumPrefixLength'] && this.listOpen).subscribe(() => this.closeList()),
            textValue.filter(x => nullOrEmpty(x[0]) && !nullOrEmpty(x[1])).subscribe(() => this.triggerEvent('ValueEntered')),
            textValue.filter(x => !nullOrEmpty(x[0]) && nullOrEmpty(x[1])).subscribe(() => this.triggerEvent('ValueCleared')),
            checkValueTrigger
                .filter(x => x === '')
                .withLatestFrom(this.value, (_, x) => x)
                .filter(x => x !== null)
                .subscribe(() => this.select(null, true)),

            checkValueTrigger
                .filter(x => x !== '')
                // this is to ensure this runs after the menu handler, which may save us a server roundtrip
                .flatMap(x => Observable.interval(0).first().map(() => x))
                .withLatestFrom(this.value, (x, y) => ({ newDisplay: x, currentItem: y }))
                .filter(x => x.currentItem === null || x.newDisplay !== x.currentItem.displayValue)
                .switchMap(x => this.isCaseInsensitiveMatch(x.newDisplay, x.currentItem) ? Observable.of(x.currentItem) : this.getLookupItemByDisplay(x.newDisplay).takeUntil(focusIn))
                .withLatestFrom(this.value, (x, y) => ({ newValue: x, oldValue: y }))
                .subscribe(x => this.select(x.newValue, !this.itemsEquivalent(x.oldValue, x.newValue))),

            valueSet.filter(x => x.newKey === null).subscribe(() => this.select(null, false)),
            valueSet.filter(x => x.newKey !== null && (x.currentItem === null || x.newKey !== x.currentItem.keyValue))
                .switchMap(x => this.getLookupItemByKey(x.newKey))
                .subscribe(x => this.select(x, false)),

            this.value.distinctUntilChanged().subscribe(x => this.$element.triggerHandler('content.SelectedValue', x === null ? [null] : [x.keyValue])),
            this.listDisposable,
            this.processBindings(this.config),
            this.managePrimaryBinding('content.SelectedValue'),
            this.readyTrigger.filter(() => this.initialized && this.isReady()).first().subscribe(() => this.triggerEvent('Ready for Input')),
            this.readyTrigger,
            Observable.interval(0).first().subscribe(() => this.readyTrigger.next(null)),
            //rxui.applyIE8SizeAndCenteringHackIfNecessary(this.$element, this.$input)
        );

        this.initialized = true;

        this.initialize();
    }

    dispose() {
        super.dispose();
        this.listDisposable.dispose();
    }

    private isCaseInsensitiveMatch(newDisplayValue: string, currentItem: LookupItem) {
        return currentItem !== null && newDisplayValue !== null && newDisplayValue.toLowerCase() === currentItem.displayValue.toLowerCase();
    }

    private itemsEquivalent(a: LookupItem, b: LookupItem) {
        return a === null && b === null ? true
                : a === null ? false
                : b === null ? false
                : a.keyValue === b.keyValue;
    }

    private makeListItem(item: LookupItem) {
        return $('<li />').text(item.displayValue).data('lookupitem', item);
    }

    private makeList(items: LookupItem[]) {
        return items.reduce((a, x) => a.append(this.makeListItem(x)), $('<ul />'));
    }

    private showLoading() {
        div('autocompletelist-loading')
            .appendTo(this.$element);
    }

    private removeLoading() {
        this.$element.children('.autocompletelist-loading').remove();
    }

    private renderList(result: LookupResult) {
        const items = result.items;

        if (items.length === 0) {
            this.closeList();
            return;
        }

        var $list = this.makeList(items);

        var offset = this.$element.offset();
        var availableAbove = offset.top - listMargin;
        var availableBelow = $(window).height() - offset.top - this.$element.height() - listMargin;
        var direction = availableBelow > maxListHeight ? 'down'
                        : availableAbove > availableBelow ? 'up'
                        : 'down';

        var maxHeight = Math.min(direction === 'up' ? availableAbove : availableBelow, maxListHeight);

        var $popupInner = $('<div />').append($('<div />').append($list).css('max-height', maxHeight + 'px'));

        var $popup = $('<div />').append($popupInner).addClass('autocompletelist-list')
            .css('min-width', this.$element.width())
            .css('text-align', this.config['content.TextAlignment'].toLowerCase())
            .conditionalCss(this.config['content.FontFamily'], 'font-family')
            .conditionalCss(this.config['content.FontStyle'], 'font-style')
            .css('font-size', this.config['content.FontSize'] + 'px')
            .css('font-weight', this.config['content.FontWeight'].toLowerCase())
            .conditionalCss(this.config['content.Foreground'], 'color',() => Color.splitArgb(this.config['content.Foreground'])[0])
            .applyBackground(this.config['content.Background'])
            .appendTo(this.$element.closest('.runtime-application-container'))
            .position({
                my: 'left ' + (direction === 'down' ? 'top' : 'bottom'),
                at: 'left ' + (direction === 'down' ? 'bottom' : 'top'),
                of: this.$element,
                collision: 'flipfit none'
            });

        // this ugliness is to get consistent li sizing, full-row highlight, consistent scrollbar spacing, and proper row padding working simultaneously in Chrome, Edge, FF, Safari
        const verticalScrollbarsRequired = $list.parent()[0].scrollHeight > $list.parent()[0].clientHeight;
        const maxItemWidth = maxDefaultZero($list.find('li').toArray().map(x => x.scrollWidth));
        $list.find('li').css('width', '100%');
        const targetWidth = Math.max(maxItemWidth + (verticalScrollbarsRequired ? 15 : 0), this.$element.width() - 2);
        $popupInner.width(targetWidth);

        this.focusGroup.next(this.$input.add($list).add($popup).add($popupInner));

        this.listOpen = true;

        var selection = new BehaviorSubject<LookupItem>(null);

        var selectionClass = 'autocompletelist-selected';

        this.listDisposable.set(disposableSubscription(
            disposable(() => $popup.remove()),

            rxui.clickOutside($list.add(this.$element)).subscribe(() => this.closeList()),

            rxui.groupBlur(this.$input.add($list).add($popup).add($popupInner))
                .withLatestFrom(selection.skip(1),(x, y) => y)
                .withLatestFrom(this.value, (x, y) => ({ newValue: x, oldValue: y }))
                .subscribe(x => {
                    if (this.itemsEquivalent(x.oldValue, x.newValue)) {
                        this.closeList();
                        return;
                    }

                    if (x !== null) {
                        this.select(x.newValue, true);
                    }
                    else {
                        var selectedValue = items.filter(y => (y.displayValue === result.filter) || (y.displayValue.toLowerCase() === result.filter.toLowerCase()))[0] || null;
                        this.select(selectedValue, true);
                    }
                }
            ),

            rxui.groupBlur(this.$input.add($list).add($popup).add($popupInner)).subscribe(() => this.closeList()),

            rxui.filteredKeys(this.$input, [$.ui.keyCode.ESCAPE]).subscribe(e => {
                this.setText(result.filter);
                this.closeList();
                e.stopPropagation();
            }),

            rxui.key(this.$input).map(x => navKeys[x]).filter(x => x !== undefined)
                .withLatestFrom(selection.asObservable(), (x, y) => ({ nav: x, current: y }))
                .subscribe(x => {
                    var currentIndex = x.current === null ? 0 : items.indexOf(x.current) + 1;
                    const continuum = [items[items.length - 1], null, ...items, null];
                    const newSelection = continuum[currentIndex + 1 + x.nav];
                    selection.next(newSelection);
                }),

            selection.pairwise().subscribe(x => {
                if (x[0] !== null)
                    $list.find('li').toArray().map(y => $(y)).filter(y => y.data('lookupitem') === x[0])[0].removeClass(selectionClass);

                if (x[1] != null) {
                    var $item = $list.find('li').toArray().map(y => $(y)).filter(y => y.data('lookupitem') === x[1])[0];
                    $item.addClass(selectionClass);

                    this.scrollItemIntoView($item, $popup);
                }

                this.setText(x[1] === null ? result.filter : x[1].displayValue);
            }),

            rxui.mousedownleft($popup.find('ul'))
                .map(x => $(x.target))
                .map(x => <LookupItem>(x.data('lookupitem')))
                .race(rxui.filteredKeys(this.$input, [13]).withLatestFrom(selection.asObservable(), (x, y) => y))
                .subscribe(x => this.select(x, true)),

            rxui.scrollOutside($popup).first().subscribe(() => this.closeList()),

            selection
        ));
    }

    private scrollItemIntoView($item: JQuery, $popup: JQuery) {
        var st = $popup.children('div')[0].scrollTop;

        if ($item.position().top + $item.height() > st + $popup.height())
            $item[0].scrollIntoView(false);
        else if (st > $item.position().top)
            $item[0].scrollIntoView(true);
    }

    private setText(text: string) {
        this.updating = true;
        this.$input.val(text);
        this.updating = false;
    }

    private select(item: LookupItem, raiseSelected: boolean) {
        this.setText(item === null ? '' : item.displayValue);
        this.value.next(item);
        this.closeList();

        if (raiseSelected)
            this.triggerEvent('Selection Made');
    }

    private closeList() {
        this.listDisposable.set(null);
        this.listOpen = false;
        this.focusGroup.next(this.$input);
    }

    private getLookupItems(filter: string): Observable<LookupResult> {
        return this.eventAggregator.request(new FilteredLookupItemsRequested(this.fieldBindingSource('Content.SelectedValue'), this.fieldContext, filter)).map(x => new LookupResult(filter, x));
    }

    private getLookupItemByDisplay(display: string): Observable<LookupItem> {
        return this.eventAggregator.request(new LookupItemByDisplayRequested(this.fieldBindingSource('Content.SelectedValue'), this.fieldContext, display));
    }

    private getLookupItemByKey(key: string): Observable<LookupItem> {
        return this.eventAggregator.request(new LookupItemRequested(this.fieldBindingSource('Content.SelectedValue'), this.fieldContext, key));
    }

    'content.SelectedValue'(value: string) {
        this.checkInitialValueReceived();
        this.setValues.next(value);
    }

    focus() {
        this.$input.focus();
    }

    applyEnabledState(value: boolean) {
        this.$input.prop('readonly', !value);
        this.$icon.conditional(value, x => x.show()).conditional(!value, x => x.hide());
        this.readyTrigger.next(null);
    }

    tabIndexElements() {
        return this.$input;
    }
}

registerBinding('autocompletelist', AutoCompleteList);